// src/app/shared/app-init/LoadingScreen.tsx
import { SafeAreaInsets } from "@/app/hooks/useViewportInsets";
import { InitializationState } from "./AppInitializationController";
import { motion, AnimatePresence } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, RotateCw } from "lucide-react";
import { LoadingLottieIcon, LoadingLottieMode } from "@/app/shared/ui/LoadingLottieIcon";

interface LoadingScreenProps {
  state: InitializationState;
  errorMessage?: string | null;
  onRetry?: () => void;
  onReadyComplete?: () => void;
  contentInsets: SafeAreaInsets;
}

function getHolidayGreeting(): string | null {
  const now = new Date();
  const month = now.getMonth() + 1;
  const day = now.getDate();

  if ((month === 12 && day === 31) || (month === 1 && day === 1)) return "С Новым годом!";
  if (month === 1 && day === 7) return "С Рождеством!";
  if (month === 2 && day === 23) return "С 23 Февраля!";
  if (month === 3 && day === 8) return "С 8 Марта!";
  if (month === 5 && day === 9) return "С Днём Победы!";
  if (month === 9 && day === 1) return "С Днём знаний!";
  return null;
}

function getSeason(): 'winter' | 'spring' | 'summer' | 'autumn' {
  const month = new Date().getMonth() + 1;
  if (month === 12 || month <= 2) return 'winter';
  if (month >= 3 && month <= 5) return 'spring';
  if (month >= 6 && month <= 8) return 'summer';
  return 'autumn';
}

// Палитра пузырей по сезонам
const SEASON_COLORS: Record<string, string[]> = {
  winter: ['#e0f7fa', '#b3e5fc', '#81d4fa', '#f0f4f8'],
  spring: ['#fce4ec', '#f8bbd0', '#f48fb1', '#f3e5f5'],
  summer: ['#fff9c4', '#ffe082', '#ffb74d', '#fff3e0'],
  autumn: ['#ffccbc', '#ffab91', '#ff8a65', '#fbe9e7'],
};

function BubblesBackground({ season }: { season: string }) {
  const bubbles = useMemo(() => {
    const colors = SEASON_COLORS[season] || SEASON_COLORS.spring;
    return Array.from({ length: 14 }).map((_, i) => ({
      id: i,
      size: 60 + Math.random() * 120,
      x: Math.random() * 100,
      y: Math.random() * 100,
      color: colors[i % colors.length],
      duration: 12 + Math.random() * 20,
      delay: Math.random() * 5,
    }));
  }, [season]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {bubbles.map((bubble) => (
        <motion.div
          key={bubble.id}
          className="absolute rounded-full will-change-transform"
          style={{
            width: bubble.size,
            height: bubble.size,
            backgroundColor: bubble.color,
            opacity: 0.15,
            filter: 'blur(40px)',
            left: `${bubble.x}%`,
            top: `${bubble.y}%`,
          }}
          animate={{
            x: [0, 20, -15, 10, 0],
            y: [0, -15, 10, -20, 0],
            scale: [1, 1.1, 0.95, 1.05, 1],
          }}
          transition={{
            duration: bubble.duration,
            repeat: Infinity,
            delay: bubble.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

export function LoadingScreen({
  state,
  errorMessage,
  onRetry,
  onReadyComplete,
  contentInsets,
}: LoadingScreenProps) {
  const [progress, setProgress] = useState(0);
  const prevStateRef = useRef<InitializationState | null>(null);
  const readyNotifiedRef = useRef(false);
  const [readyAnimationTailFinished, setReadyAnimationTailFinished] = useState(false);

  const user = (window as any).Telegram?.WebApp?.initDataUnsafe?.user;
  const greetingName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'друг'
    : 'друг';
  const holidayGreeting = getHolidayGreeting();
  const season = getSeason();

  const isErrorState = state === InitializationState.VALIDATION_ERROR;
  const lottieMode: LoadingLottieMode = state === InitializationState.READY ? "tail" : "loop";
  const progressTarget = useMemo(() => {
    if (state === InitializationState.VALIDATING) return 55;
    if (state === InitializationState.CONNECTING) return 100;
    if (state === InitializationState.READY) return 100;
    return null;
  }, [state]);

  const fallbackStatusText = useMemo(() => {
    if (state === InitializationState.VALIDATING) return "Авторизация...";
    if (state === InitializationState.CONNECTING) return "Подключение к серверу...";
    if (state === InitializationState.READY) return "Готово!";
    if (state === InitializationState.VALIDATION_ERROR) return "Ошибка валидации";
    return "Инициализация...";
  }, [state]);

  const statusText = isErrorState
    ? errorMessage?.trim() || fallbackStatusText
    : fallbackStatusText;

  useEffect(() => {
    const prevState = prevStateRef.current;
    const cameFromError = prevState === InitializationState.VALIDATION_ERROR;

    if (state === InitializationState.VALIDATING && cameFromError) {
      setProgress(0);
    }
    if (state === InitializationState.CONNECTING) {
      setProgress((prev) => Math.max(prev, 55));
    }
    if (state === InitializationState.READY) {
      setProgress((prev) => Math.max(prev, 90));
    }
    prevStateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (state !== InitializationState.READY) {
      readyNotifiedRef.current = false;
      setReadyAnimationTailFinished(false);
    }
  }, [state]);

  useEffect(() => {
    if (isErrorState || progressTarget === null) return;
    const timer = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= progressTarget) return prev;
        const delta = progressTarget - prev;
        const stepMultiplier = state === InitializationState.READY ? 0.38 : 0.08;
        const minStep = state === InitializationState.READY ? 3 : 0.6;
        const step = Math.max(minStep, delta * stepMultiplier);
        return Math.min(progressTarget, prev + step);
      });
    }, 60);
    return () => window.clearInterval(timer);
  }, [isErrorState, progressTarget]);

  useEffect(() => {
    if (
      state !== InitializationState.READY ||
      progress < 100 ||
      !readyAnimationTailFinished ||
      readyNotifiedRef.current
    ) return;
    readyNotifiedRef.current = true;
    const timer = window.setTimeout(() => {
      onReadyComplete?.();
    }, 120);
    return () => window.clearTimeout(timer);
  }, [state, progress, readyAnimationTailFinished, onReadyComplete]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        paddingTop: `${contentInsets.top}px`,
        paddingBottom: `${contentInsets.bottom}px`,
        paddingLeft: `${contentInsets.left}px`,
        paddingRight: `${contentInsets.right}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--background)",
        zIndex: 999,
      }}
    >
      <BubblesBackground season={season} />

      <div className="relative z-10 flex w-full max-w-xs flex-col items-center gap-10">
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative flex h-24 w-24 items-center justify-center"
        >
          <motion.div
            animate={{
              scale: isErrorState ? [1, 1.1, 1] : [1, 1.2, 1],
              opacity: isErrorState ? 0.4 : [0.16, 0.3, 0.16],
            }}
            transition={{
              duration: isErrorState ? 2 : 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className={`absolute inset-0 rounded-full blur-3xl ${
              isErrorState ? "bg-destructive/40" : "bg-accent/40"
            }`}
          />
          <LoadingLottieIcon
            mode={lottieMode}
            isError={isErrorState}
            sizeClassName="h-24 w-24"
            onTailComplete={() => {
              if (state === InitializationState.READY) {
                setReadyAnimationTailFinished(true);
              }
            }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="flex flex-col items-center gap-2 text-center"
        >
          <span className="text-xl font-semibold text-foreground">
            Привет, {greetingName}
          </span>
          {holidayGreeting && (
            <motion.span
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.3 }}
              className="text-sm font-medium text-accent"
            >
              {holidayGreeting}
            </motion.span>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="flex w-56 flex-col items-center gap-3"
        >
          <div className="h-[2px] w-full overflow-hidden rounded-full bg-muted">
            <motion.div
              className={`h-full rounded-full transition-colors duration-300 ${
                isErrorState ? "bg-destructive" : "bg-foreground"
              }`}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.15, ease: "linear" }}
            />
          </div>
          <div className="flex min-h-5 flex-col items-center gap-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={statusText}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.2 }}
                className={`flex items-center gap-1.5 text-center text-[13px] font-medium tracking-wide ${
                  isErrorState ? "text-destructive" : "text-muted-foreground"
                }`}
              >
                {isErrorState ? <AlertCircle className="h-3.5 w-3.5" /> : null}
                {statusText}
              </motion.div>
            </AnimatePresence>

            <AnimatePresence>
              {isErrorState && onRetry ? (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -10 }}
                  onClick={onRetry}
                  className="mt-2 flex items-center gap-2 rounded-full border border-transparent bg-muted/50 px-5 py-2 text-[13px] font-medium text-foreground transition-all hover:border-border hover:bg-muted active:scale-95"
                >
                  <RotateCw className="h-3.5 w-3.5" />
                  Повторить
                </motion.button>
              ) : null}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
}